import React, { useEffect } from 'react';
import PageHeader from '../components/common/PageHeader';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Chip,
  Divider,
  Skeleton,
} from '@mui/material';
import { getCompanyGHG, CompanyGHG } from '../services/ghgReportingApi';
import { formatNumber } from '../utils/numberFormat';
import { format } from 'date-fns';
import { useAuthStore } from '../store/authStore';
import { AccountType } from '../types/auth';
import LicenseeFilter from '../components/common/LicenseeFilter';
import {
  buildTransportModeChartData,
  buildDateRangeOptions,
  formatEmissions,
} from './_shared/ghgStatsUtils';
import OrganizationalEmissionsCards from './_shared/OrganizationalEmissionsCards';
import TransportModeChartPair from './_shared/TransportModeChartPair';
import ExportButtonMenu from './_shared/ExportButtonMenu';
import PeriodFilterToggle from './_shared/PeriodFilterToggle';
import GhgLoadingSkeleton from './_shared/GhgLoadingSkeleton';
import GhgDailyBreakdownTable from './_shared/GhgDailyBreakdownTable';
import GhgMonthlyTable from './_shared/GhgMonthlyTable';
import { useCompanyPageState } from './_shared/useCompanyPageState';
import { useGhgExportHandlers } from './_shared/useGhgExportHandlers';

const CompanyGHGPage: React.FC = () => {
  const { companyId: paramCompanyId } = useParams<{ companyId: string }>();
  const { user } = useAuthStore();
  const {
    data, setData,
    loading, setLoading,
    error, setError,
    filterMode, setFilterMode,
    selectedYear, setSelectedYear,
    fromDate, setFromDate,
    toDate, setToDate,
    availableYears, setAvailableYears,
    exporting, setExporting,
    selectedCompanyId, setSelectedCompanyId,
    selectedMonth, setSelectedMonth,
    dailyData, setDailyData,
    loadingDaily, setLoadingDaily,
    dailyBreakdownRef,
  } = useCompanyPageState<CompanyGHG>();

  const isPlatformAdmin = user?.accountType === AccountType.MasterAdmin || user?.accountType === AccountType.CarbonCareAdmin;

  // Use selectedCompanyId if set, otherwise fallback to params or user's companyId
  const companyId = selectedCompanyId || (paramCompanyId ? Number.parseInt(paramCompanyId) : user?.companyId);

  const { handleExcelExport, handlePdfExport } = useGhgExportHandlers({
    filterMode,
    fromDate,
    toDate,
    selectedYear,
    setExporting,
    setError,
    companyId,
  });

  // Generate fallback years
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    setAvailableYears([currentYear, currentYear - 1, currentYear - 2]);
  }, []);



  useEffect(() => {
    if (companyId) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, companyId, fromDate, toDate, filterMode]);

  const fetchData = async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      setSelectedMonth(null)
      setError(null);

      const options = buildDateRangeOptions(filterMode, fromDate, toDate, selectedYear);

      const result = await getCompanyGHG(companyId, options);
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load company GHG reporting');
    } finally {
      setLoading(false);
    }
  };

  const handleMonthClick = async (month: { year: number; month: number; month_name: string }) => {
    // Toggle: if same month is clicked, collapse it
    if (selectedMonth?.year === month.year && selectedMonth?.month === month.month) {
      setSelectedMonth(null);
      setDailyData([]);
      return;
    }

    setSelectedMonth(month);
    setLoadingDaily(true);

    try {
      // Calculate first and last day of the selected month
      const firstDay = format(new Date(month.year, month.month - 1, 1), 'yyyy-MM-dd');
      const lastDay = format(new Date(month.year, month.month, 0), 'yyyy-MM-dd');

      // Fetch daily data for the month
      const result = await getCompanyGHG(companyId!, {
        fromDate: firstDay,
        toDate: lastDay
      });

      // Filter daily data for the selected month
      const monthlyDaily = result.daily.filter((day: any) =>
        day.year === month.year && day.month === month.month
      );

      setDailyData(monthlyDaily);

      // Scroll to daily breakdown after data loads
      setTimeout(() => {
        dailyBreakdownRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      console.error('Failed to load daily GHG data:', err);
      setDailyData([]);
    } finally {
      setLoadingDaily(false);
    }
  };

  if (!loading && error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const hasData = data?.yearly;

  // Precompute bar chart data for transport modes (raw values for recharts auto-scale)
  const transportModeChartData = buildTransportModeChartData(
    data?.yearly?.transport_chain_elements as any,
  );

  // Only include modes with actual emissions so log scale works (log(0) is undefined)
  const tonsChartData = transportModeChartData.filter(d => d.TONS > 0);

  return (
    <Box>
      {/* Header */}
      <PageHeader
        description={
          data?.company?.name
            ? `GHG emissions report for ${data.company.name}.`
            : 'GHG emissions report for your licensee.'
        }
        actions={
          <ExportButtonMenu
            onRefresh={fetchData}
            onExcelExport={handleExcelExport}
            onPdfExport={handlePdfExport}
            exporting={exporting}
          />
        }
      />
      {loading ? (
        <Box display="flex" gap={1} mb={2}>
          <Skeleton variant="rounded" width={48} height={22} sx={{ borderRadius: '12px' }} />
          <Skeleton variant="rounded" width={56} height={22} sx={{ borderRadius: '12px' }} />
        </Box>
      ) : hasData && data?.company && (
        <Box display="flex" gap={1} mb={2}>
          <Chip label={data.company.licence_type} size="small" />
          {data.company.is_demo && <Chip label="Demo" size="small" color="warning" />}
        </Box>
      )}
      {/* Filters Bar */}
      <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', mb: 3 }}>
        {isPlatformAdmin && (
          <>
            <LicenseeFilter
              value={selectedCompanyId || companyId || ''}
              onChange={(id) => setSelectedCompanyId(Number(id))}
              label="Select Company"
              required
              sx={{ minWidth: 250 }}
            />
            <Divider orientation="vertical" flexItem />
          </>
        )}
        <PeriodFilterToggle
          filterMode={filterMode}
          onFilterModeChange={setFilterMode}
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
          years={Array.from(new Set([...availableYears, ...(data?.availableYears ?? [])])).sort((a, b) => b - a)}
          fromDate={fromDate}
          toDate={toDate}
          onFromDateChange={setFromDate}
          onToDateChange={setToDate}
        />
      </Paper>

      {/* Loading skeleton — org cards + 2 charts */}
      {loading && <GhgLoadingSkeleton />}

      {/* Organizational Level Emissions - Green Tiles + Transport Mode Charts */}
      {!loading && (
      <>
      <OrganizationalEmissionsCards
        items={[
          { label: 'Total Emissions (TOT)', value: `${formatEmissions(data?.yearly?.organizational_level?.tot_emissions ?? 0)}`, unit: 'Tonnes', subtitle: '' },
          { label: 'Operational Emissions (OPS)', value: `${formatEmissions(data?.yearly?.organizational_level?.ops_emissions ?? 0)}`, unit: 'Tonnes', subtitle: '' },
          { label: 'Energy Provision (ENE)', value: `${formatEmissions(data?.yearly?.organizational_level?.ene_emissions ?? 0)}`, unit: 'Tonnes', subtitle: '' },
          { label: 'Emission Intensity (gr/tkm)', value: formatNumber(data?.yearly?.organizational_level?.ei ?? 0, 2), unit: 'gr/tkm', subtitle: '' },
          { label: 'TKM', value: formatNumber(data?.yearly?.organizational_level?.tkm ?? 0), unit: 'tkm', subtitle: 'Tonne-kilometers' },
        ]}
      />

      {/* Emissions by Transport Mode - Bar Charts */}
          <TransportModeChartPair
            allData={transportModeChartData}
            tonsData={tonsChartData}
          />
      </>
      )}

      {/* Monthly Breakdown — shows skeleton while loading, data once loaded */}
      <GhgMonthlyTable
        loading={loading}
        months={data?.monthly ?? []}
        selectedMonth={selectedMonth}
        onMonthClick={handleMonthClick}
      />

      {/* Daily Breakdown for Selected Month */}
      {selectedMonth && (
        <Paper ref={dailyBreakdownRef} sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Daily Emissions - {selectedMonth.month_name}
          </Typography>
          <GhgDailyBreakdownTable loading={loadingDaily} data={dailyData} />
        </Paper>
      )}
    </Box>
  );
};

export default CompanyGHGPage;
